import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea, 
  Chip,
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Grid,
  Button
} from '@mui/material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import ClearIcon from '@mui/icons-material/Clear';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

// Date formatting
const formatDate = (dateString) => {
  try {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, 'MMM dd, yyyy HH:mm', { locale: enUS });
  } catch (e) {
    console.error('Date format error:', e);
    return 'Invalid date';
  }
};

// Conversation card component
const ConversationCard = ({ conversation, onClick, searchQuery, favorites = [], onToggleFavorite }) => {
  const isFavorite = favorites.includes(conversation.id);

  // Highlight search keywords
  const highlightText = (text, query) => {
    if (!query || !text || query.trim() === '') return text;
    
    try {
      const keywords = query.trim().split(/\s+/);
      let highlightedText = text;
      keywords.forEach(keyword => {
        if (keyword) {
          const regex = new RegExp(`(${keyword})`, 'gi');
          highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
        }
      });
      return highlightedText;
    } catch (e) {
      console.error('Highlight error:', e);
      return text;
    }
  };
  
  const contentWithHighlight = searchQuery && conversation.content 
    ? highlightText(conversation.content.substring(0, 100) + (conversation.content.length > 100 ? '...' : ''), searchQuery)
    : conversation.content 
      ? conversation.content.substring(0, 100) + (conversation.content.length > 100 ? '...' : '') 
      : 'No content';

  return (
    <Card className="conversation-card" sx={{ mb: 2 }} elevation={2}>
      <CardActionArea onClick={() => onClick(conversation)}>
        <CardContent>
          <Typography variant="h6" component="div" gutterBottom>
            {conversation.title}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {formatDate(conversation.timestamp)}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 1.5 }} 
            dangerouslySetInnerHTML={{ __html: contentWithHighlight }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip 
              label={conversation.sender || 'unknown'} 
              size="small" 
              color={conversation.sender === 'user' ? 'primary' : 'secondary'}
              sx={{ mb: 0.5 }}
            />
            {conversation.inContext !== undefined && (
              <Chip 
                label={conversation.inContext ? 'In Context' : 'Out of Context'} 
                size="small" 
                variant="outlined"
                sx={{ mb: 0.5 }}
              />
            )}
            {conversation.imageData && (
              <Chip 
                label="Has Image" 
                size="small" 
                color="info"
                sx={{ mb: 0.5 }}
              />
            )}
            <Chip 
              icon={isFavorite ? <StarIcon /> : <StarOutlineIcon />} 
              label={isFavorite ? 'Favorited' : 'Favorite'}
              size="small" 
              color={isFavorite ? "warning" : "default"}
              variant="outlined"
              sx={{ mb: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite && onToggleFavorite(conversation.id);
              }}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// Search panel component
const SearchPanel = ({ searchQuery, onSearch }) => {
  const [keyword, setKeyword] = useState(searchQuery || '');

  const handleKeywordChange = (event) => {
    const value = event.target.value;
    setKeyword(value);
    onSearch(value);
  };

  const clearSearch = () => {
    setKeyword('');
    onSearch('');
  };

  return (
    <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Search Favorites
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Search Keywords"
            value={keyword}
            onChange={handleKeywordChange}
            size="small"
            InputProps={{
              endAdornment: keyword && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={clearSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Button
            variant="outlined"
            size="small"
            onClick={clearSearch}
            startIcon={<ClearIcon />}
            fullWidth
          >
            Clear Search
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

// Main Favorites View component
const EnFavoritesView = ({ 
  conversations, 
  onConversationClick, 
  searchQuery, 
  favorites,
  onToggleFavorite,
  onSearch
}) => {
  // Filter conversations to show only favorites
  const getFavoriteConversations = () => {
    let filtered = conversations.filter(conversation => 
      favorites && favorites.includes(conversation.id)
    );

    // Apply search filter
    if (searchQuery && searchQuery.trim()) {
      const keywords = searchQuery.trim().toLowerCase().split(/\s+/);
      filtered = filtered.filter(conversation => {
        const content = (conversation.content || '').toLowerCase();
        const title = (conversation.title || '').toLowerCase();
        return keywords.some(keyword => 
          content.includes(keyword) || title.includes(keyword)
        );
      });
    }

    // Sort by timestamp (newest first)
    return filtered.sort((a, b) => 
      new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
    );
  };

  const favoriteConversations = getFavoriteConversations();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Favorites
      </Typography>
      
      <SearchPanel 
        searchQuery={searchQuery}
        onSearch={onSearch}
      />
      
      {favoriteConversations.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <StarOutlineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No favorite conversations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery ? 
              'Try adjusting your search criteria or add more conversations to favorites' :
              'Mark conversations as favorites to see them here'
            }
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {favoriteConversations.length} favorite conversation{favoriteConversations.length !== 1 ? 's' : ''} found
          </Typography>
          
          {favoriteConversations.map((conversation, index) => (
            <ConversationCard 
              key={conversation.id || `fav-${index}`} 
              conversation={conversation} 
              onClick={onConversationClick}
              searchQuery={searchQuery}
              favorites={favorites}
              onToggleFavorite={onToggleFavorite}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default EnFavoritesView; 